import React from 'react';
import { Card } from '@/components/ui/card';
import { GitBranch, Check, AlertTriangle, XCircle, MinusCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A' };

const META = {
  ok: { color: C.green, Icon: Check },
  bound: { color: C.green, Icon: Check },
  hit: { color: C.red, Icon: AlertTriangle },
  refused: { color: C.red, Icon: XCircle },
  blocked: { color: C.red, Icon: XCircle },
  error: { color: C.red, Icon: XCircle },
  held: { color: C.amber, Icon: AlertTriangle },
  referred: { color: C.amber, Icon: AlertTriangle },
  refer: { color: C.amber, Icon: AlertTriangle },
  flagged: { color: C.amber, Icon: AlertTriangle },
  skipped: { color: C.slate, Icon: MinusCircle },
};

/** The named stages that produced this decision, in the order they ran. Every caller — the broker
 *  form, the class form, the email channel, the rehearsal engines — runs this same path, so the
 *  trace reads the same wherever the risk came in. */
export default function DecisionPath({ trace, modelVersion, serviceVersion }) {
  if (!trace?.length) return null;
  const total = trace.reduce((n, s) => n + (s.ms || 0), 0);
  return (
    <Card className="rounded-md bg-white p-4" data-testid="decision-path-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <div className="flex items-center gap-2 mb-1">
        <GitBranch className="w-4 h-4" style={{ color: C.navy }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
          Decision Path
        </h3>
        <span className="text-[10px] font-mono ml-auto" style={{ color: C.slate }}
          data-testid="decision-path-total-ms">{total} ms</span>
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>
        Every gate that was asked, in the order it was asked, on the one path every channel runs.
      </p>
      <div className="space-y-1">
        {trace.map((s, i) => {
          const m = META[s.status] || META.ok;
          return (
            <div key={`${s.stage}-${i}`} className="flex items-start gap-2 py-1"
              data-testid={`decision-stage-${s.stage}`}
              style={{ borderTop: i ? '1px solid rgba(136,150,168,0.10)' : 'none' }}>
              <m.Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: m.color }} />
              <span className="text-[11px] font-mono w-24 shrink-0" style={{ color: C.navy }}>
                {s.stage}
              </span>
              <span className="text-[11px] flex-1" style={{ color: C.navy }}>{s.summary}</span>
              <span className="text-[10px] font-mono shrink-0" style={{ color: C.slate }}>
                {s.ms}ms
              </span>
            </div>
          );
        })}
      </div>
      {(modelVersion || serviceVersion) && (
        <p className="text-[10px] font-mono mt-3 pt-2 break-all" style={{ color: C.slate, borderTop: '1px solid rgba(136,150,168,0.12)' }}
          data-testid="decision-path-versions">
          {serviceVersion ? `${serviceVersion} · ` : ''}{modelVersion}
        </p>
      )}
    </Card>
  );
}
